/**
 * Leviathan Web - Tank Form Component
 * Quasar Prime LLC
 * Reusable form for creating and editing storage tanks
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';

interface TankFormData {
  tankNumber: string;
  name: string;
  location: string;
  tankType: 'ABOVE_GROUND' | 'UNDERGROUND' | 'PORTABLE' | 'TRAILER';
  capacity: string;
  currentLevel: string;
  minLevel: string;
  maxLevel: string;
  productId: string;
  status: 'ACTIVE' | 'MAINTENANCE' | 'OUT_OF_SERVICE' | 'DECOMMISSIONED';
  lastInspectionDate: string;
  nextInspectionDue: string;
  installDate: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  notes: string;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  category: string;
}

interface TankFormProps {
  tankId?: string;
  initialData?: Partial<TankFormData>;
  onSuccess?: () => void;
}

export default function TankForm({ tankId, initialData, onSuccess }: TankFormProps) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState<Product[]>([]);

  const [formData, setFormData] = useState<TankFormData>({
    tankNumber: '',
    name: '',
    location: '',
    tankType: 'ABOVE_GROUND',
    capacity: '',
    currentLevel: '0',
    minLevel: '',
    maxLevel: '',
    productId: '',
    status: 'ACTIVE',
    lastInspectionDate: '',
    nextInspectionDue: '',
    installDate: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    notes: '',
    ...initialData,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory?category=FUEL&isActive=true`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // If capacity changes, auto-set maxLevel to 90% of capacity (unless maxLevel is already set)
    if (name === 'capacity') {
      const capacityValue = parseFloat(value);
      if (!isNaN(capacityValue) && capacityValue > 0) {
        // Auto-set maxLevel to 90% of capacity if maxLevel is empty or when creating new tank
        const defaultMaxLevel = Math.round(capacityValue * 0.9 * 100) / 100;
        setFormData((prev) => ({
          ...prev,
          capacity: value,
          maxLevel: prev.maxLevel === '' ? String(defaultMaxLevel) : prev.maxLevel,
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate max level doesn't exceed capacity
    const capacityNum = parseFloat(formData.capacity);
    const maxLevelNum = parseFloat(formData.maxLevel);
    if (!isNaN(capacityNum) && !isNaN(maxLevelNum) && maxLevelNum > capacityNum) {
      setError('Max level cannot exceed tank capacity');
      return;
    }

    // Validate current level doesn't exceed max level
    const currentLevelNum = parseFloat(formData.currentLevel);
    if (!isNaN(maxLevelNum) && !isNaN(currentLevelNum) && currentLevelNum > maxLevelNum) {
      setError('Current level cannot exceed max level');
      return;
    }

    setIsLoading(true);

    try {
      // Build payload, converting string numbers to actual numbers
      const payload: Record<string, unknown> = {
        ...formData,
        capacity: formData.capacity ? parseFloat(formData.capacity) : null,
        currentLevel: formData.currentLevel ? parseFloat(formData.currentLevel) : 0,
        minLevel: formData.minLevel ? parseFloat(formData.minLevel) : null,
        maxLevel: formData.maxLevel ? parseFloat(formData.maxLevel) : null,
        lastInspectionDate: formData.lastInspectionDate || null,
        nextInspectionDue: formData.nextInspectionDue || null,
        installDate: formData.installDate || null,
        productId: formData.productId || null,
      };

      // Remove empty strings, convert to null
      Object.keys(payload).forEach((key) => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });

      const url = tankId ? `${API_URL}/api/tanks/${tankId}` : `${API_URL}/api/tanks`;

      const method = tankId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/tanks');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tank');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Basic Information */}
      <FormSection title="Tank Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Tank Number">
            <input
              type="text"
              name="tankNumber"
              value={formData.tankNumber}
              onChange={handleChange}
              placeholder="Auto-generated if empty (e.g., T-001)"
              className="form-input"
            />
          </FormField>

          <FormField label="Tank Name" required>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Main Diesel Storage"
              className="form-input"
            />
          </FormField>

          <FormField label="Tank Type" required>
            <select
              name="tankType"
              value={formData.tankType}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="ABOVE_GROUND">Above Ground</option>
              <option value="UNDERGROUND">Underground</option>
              <option value="PORTABLE">Portable</option>
              <option value="TRAILER">Trailer</option>
            </select>
          </FormField>

          <FormField label="Status">
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-input"
            >
              <option value="ACTIVE">Active</option>
              <option value="MAINTENANCE">Maintenance</option>
              <option value="OUT_OF_SERVICE">Out of Service</option>
              <option value="DECOMMISSIONED">Decommissioned</option>
            </select>
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Location">
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., North Yard, Bay 3"
                className="form-input"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Capacity & Levels */}
      <FormSection title="Capacity & Levels">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FormField label="Capacity (gallons)" required>
            <input
              type="number"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              step="0.01"
              min="0"
              required
              placeholder="e.g., 10000"
              className="form-input"
            />
          </FormField>

          <FormField label="Current Level (gallons)">
            <input
              type="number"
              name="currentLevel"
              value={formData.currentLevel}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0"
              className="form-input"
            />
          </FormField>

          <FormField label="Min Level (gallons)">
            <input
              type="number"
              name="minLevel"
              value={formData.minLevel}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="Alert threshold"
              className="form-input"
            />
          </FormField>

          <FormField label="Max Level (gallons)">
            <input
              type="number"
              name="maxLevel"
              value={formData.maxLevel}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="Usually = capacity"
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Product Assignment */}
      <FormSection title="Product Assignment">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Product Stored">
            <select
              name="productId"
              value={formData.productId}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">-- Select Product --</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} ({product.sku})
                </option>
              ))}
            </select>
            <p className="text-text-tertiary text-xs font-mono mt-1">
              Only fuel products are shown
            </p>
          </FormField>
        </div>
      </FormSection>

      {/* Inspection & Maintenance */}
      <FormSection title="Inspection & Maintenance">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Install Date">
            <input
              type="date"
              name="installDate"
              value={formData.installDate}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="Last Inspection">
            <input
              type="date"
              name="lastInspectionDate"
              value={formData.lastInspectionDate}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="Next Inspection Due">
            <input
              type="date"
              name="nextInspectionDue"
              value={formData.nextInspectionDue}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Equipment Details */}
      <FormSection title="Equipment Details">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Manufacturer">
            <input
              type="text"
              name="manufacturer"
              value={formData.manufacturer}
              onChange={handleChange}
              placeholder="e.g., Highland Tank"
              className="form-input"
            />
          </FormField>

          <FormField label="Model">
            <input
              type="text"
              name="model"
              value={formData.model}
              onChange={handleChange}
              placeholder="Tank model"
              className="form-input"
            />
          </FormField>

          <FormField label="Serial Number">
            <input
              type="text"
              name="serialNumber"
              value={formData.serialNumber}
              onChange={handleChange}
              placeholder="Serial/ID number"
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Notes */}
      <FormSection title="Notes">
        <FormField label="Internal Notes">
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Internal notes about this tank..."
            className="form-input resize-none"
          />
        </FormField>
      </FormSection>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/tanks')}
          className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative group"
          style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
        >
          <span className="relative z-10">
            {isLoading ? 'Saving...' : tankId ? 'Update Tank' : 'Create Tank'}
          </span>
          <div
            className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ mixBlendMode: 'overlay' }}
          />
        </button>
      </div>
    </form>
  );
}

/**
 * Form section with geometric panel styling
 */
function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Form field with label
 */
function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label} {required && <span className="text-amber">*</span>}
      </label>
      {children}
    </div>
  );
}
